/*
    © 2023 EcomGraduates.com
    https://www.ecomgraduates.com
*/

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector("#predictive-search");
    this.alert = this.querySelector("#predictive-search-alert");
    this.footer = this.closest("#offcanvas-search").querySelector(".offcanvas-footer");
    this.popularProducts = this.closest("#offcanvas-search").querySelector("#search-popular-products-wrapper");
    this.speechBtn = this.querySelector(".btn-search-by-voice");

    this.input.addEventListener(
      "input",
      this.debounce((event) => {
        this.onChange();
      }, 300).bind(this)
    );

    document.querySelector("#offcanvas-search")?.addEventListener("shown.bs.offcanvas", () => {
      this.input.focus();
    });

    if (window.SpeechRecognition || window.webkitSpeechRecognition) {
      this.speechBtn?.addEventListener("click", () => {
        this.speechRecognition();
      });
    } else {
      this.speechBtn.remove();
    }

    document.querySelectorAll("#offcanvas-search .btn-atc").forEach((btn) => {
      btn.addEventListener("click", () => {
        setTimeout(() => {
          bootstrap.Offcanvas.getOrCreateInstance("#offcanvas-search").hide();
        }, 300);
      });
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    this.footer.querySelector('[name="q"]').value = searchTerm;
    this.footer.querySelector(".btn").textContent = `${this.footer.querySelector(".btn").dataset.textSearchFor} "${searchTerm}"`;

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  async getSearchResults(searchTerm) {
    // Wait for Constructor.io client to be available from constructor-search.js
    if (!window.constructorio || !window.constructorio.autocomplete) {
      return this.getShopifySearchResults(searchTerm);
    }

    try {
      // Use the initialized Constructor.io client from constructor-search.js
      const constructorClient = window.constructorio;

      // Official API call following exact documentation
      const response = await constructorClient.autocomplete.getAutocompleteResults(searchTerm, {
        resultsPerSection: {
          Products: 8,
          "Search Suggestions": 5,
        },
        numResults: 8,
        filters: {},
      });

      this.renderConstructorResults(response, searchTerm);
      this.open();

      // Official tracking for autocomplete - trackInputFocus
      if (constructorClient.tracker) {
        constructorClient.tracker.trackInputFocus({
          term: searchTerm,
        });
      }
    } catch (error) {
      console.warn("Constructor.io autocomplete failed, falling back to Shopify:", error);
      return this.getShopifySearchResults(searchTerm);
    }
  }

  renderConstructorResults(response, searchTerm) {
    if (!response.sections) {
      return;
    }

    this.results.innerHTML = "";

    if (response.sections["Search Suggestions"]) {
      const suggestionsHtml = response.sections["Search Suggestions"]
        .map((suggestion, index) => {
          const suggestionValue = suggestion.value || suggestion.data?.value || suggestion;
          return `
            <li>
              <a href="/search?q=${encodeURIComponent(suggestionValue)}" 
                 class="link-body-emphasis text-decoration-none autocomplete-suggestion"
                 data-suggestion="${suggestionValue}"
                 data-index="${index}"
                 data-cnstrc-item-section="Search Suggestions"
                 data-cnstrc-item-id="${suggestionValue}"
                 data-cnstrc-item-name="${suggestionValue}">
                ${suggestionValue}
              </a>
            </li>
          `;
        })
        .join("");

      if (suggestionsHtml) {
        this.results.innerHTML += `
          <div class="mt-4 mb-3">
            <h6 class="h6 fw-bold mb-2 border-bottom pb-2">Suggestions</h6>
            <ul class="list-unstyled">${suggestionsHtml}</ul>
          </div>
        `;
      }
    }

    if (response.sections.Products && response.sections.Products.length > 0) {
      // Transform Constructor.io products to match the structure used in search/browse
      const transformedProducts = response.sections.Products.map((product, index) => {
        const data = product.data || product;
        const handle = data.handle || data.slug || "";

        return {
          handle: handle,
          title: product.value || data.title || "Untitled",
          url: data.url || `/products/${handle}`,
          id: data.id,
          price: data.price || 0,
          compare_at_price: data.compareAtPrice || 0,
          available: data.available !== false,
          featured_image: {
            src: data.image_url || "",
            alt: product.value || data.title || "",
          },
          constructorData: { data: data },
          tags: [
            ...(data.group_ids || []),
            ...(data.tags || []),
            data.preorder_tag && "preorder",
            data.waterproof_tag && "waterproof",
            data.bestseller_tag && "bestseller",
            data.new_tag && "new",
          ].filter(Boolean),
        };
      });

      // Use ConstructorUtils to render products with proper fake discount logic
      const productsHtml = transformedProducts
        .map((product, index) => {
          if (!window.ConstructorUtils) {
            return this._renderFallbackProduct(product);
          }

          return this._renderOffcanvasProduct(product, index);
        })
        .join("");

      this.results.innerHTML += `
        <div class="mt-4">
          <h6 class="h6 fw-bold mb-2 border-bottom pb-2">Products</h6>
          <ul class="product-list list-unstyled mb-0">${productsHtml}</ul>
        </div>
      `;

      this.results.querySelectorAll(".btn-atc").forEach((button) => {
        button.addEventListener("click", (e) => {
          const productUrl = e.target.dataset.productUrl;
          if (productUrl) {
            window.location.href = productUrl;
          }
        });
      });
    }

    this.addAutocompleteTrackingListeners(searchTerm);
  }

  _renderOffcanvasProduct(product, index) {
    // Use ConstructorUtils to get fake discount and pricing
    const data = product.constructorData?.data || {};
    const basePrice = product.price || 0;
    const baseComparePrice = product.compare_at_price || 0;

    const fakeDiscountResult = window.ConstructorUtils.generateFakeDiscountMessage(product, baseComparePrice, basePrice);

    const finalPrice = fakeDiscountResult.adjustedPrice;
    const originalPrice = fakeDiscountResult.adjustedComparePrice;
    const isOnSale = originalPrice && originalPrice > finalPrice;

    const formattedPrice = window.ConstructorUtils.formatMoney(finalPrice);
    const formattedComparePrice = isOnSale ? window.ConstructorUtils.formatMoney(originalPrice) : "";

    return `
      <li class="product-item py-3" 
          data-cnstrc-item-id="${product.id || product.handle}" 
          data-cnstrc-item-name="${product.title}" 
          data-cnstrc-item-section="Products">
        <div class="row align-items-center mx-n3">
          <div class="col-4 px-3">
            <a href="${product.url}">
              <img 
                class="product-item-img img-fluid me-3 rounded"
                src="${product.featured_image?.src || ""}"
                alt="${product.title}"
                width="600"
                height="600"
                loading="lazy"
                style="aspect-ratio: 1; object-fit: cover;">
            </a>
          </div>
          <div class="col-8 px-3 text-start">
            <h4 class="product-item-title h6 mb-1 text-start">
              <a href="${product.url}" class="link-dark product-link">
                ${product.title}
              </a>
            </h4>
            <p class="product-item-price small mb-2 text-start">
              ${
                formattedComparePrice
                  ? `<span class="product-item-price-compare text-dark me-1">
                   <s>${formattedComparePrice}</s>
                 </span>`
                  : ""
              }
              <span class="product-item-price-final ${isOnSale ? "text-danger" : ""}">
                ${formattedPrice}
              </span>
            </p>
            ${fakeDiscountResult.shouldShow ? fakeDiscountResult.messageHTML : ""}
            <div class="form-wrapper text-start mt-2">
              <div class="d-flex justify-content-start">
                <button
                  class="btn-atc btn btn-sm btn-primary px-4 flex-shrink-0" 
                  type="button"
                  data-product-url="${product.url}"
                  ${!product.available ? "disabled" : ""}>
                  ${product.available ? "ADD" : "Sold Out"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  _renderFallbackProduct(product) {
    // Fallback rendering if ConstructorUtils is not available
    const formattedPrice = product.price ? `$${parseFloat(product.price).toFixed(2)}` : "";
    const formattedComparePrice = product.compare_at_price ? `$${parseFloat(product.compare_at_price).toFixed(2)}` : "";

    return `
      <li class="product-item py-3" 
          data-cnstrc-item-id="${product.id || product.handle}" 
          data-cnstrc-item-name="${product.title}" 
          data-cnstrc-item-section="Products">
        <div class="row align-items-center mx-n3">
          <div class="col-4 px-3">
            <a href="${product.url}">
              <img 
                class="product-item-img img-fluid me-3 rounded"
                src="${product.featured_image?.src || ""}"
                alt="${product.title}"
                width="600"
                height="600"
                loading="lazy"
                style="aspect-ratio: 1; object-fit: cover;">
            </a>
          </div>
          <div class="col-8 px-3 text-start">
            <h4 class="product-item-title h6 mb-1 text-start">
              <a href="${product.url}" class="link-dark product-link">
                ${product.title}
              </a>
            </h4>
            <p class="product-item-price small mb-3 text-start">
              ${
                formattedComparePrice && formattedComparePrice !== formattedPrice
                  ? `<span class="product-item-price-compare text-dark me-1">
                   <s>${formattedComparePrice}</s>
                 </span>`
                  : ""
              }
              <span class="product-item-price-final text-danger">
                ${formattedPrice}
              </span>
            </p>
            <div class="form-wrapper text-start">
              <div class="d-flex justify-content-start">
                <button
                  class="btn-atc btn btn-sm btn-primary px-4 flex-shrink-0" 
                  type="button"
                  data-product-url="${product.url}"
                  ${!product.available ? "disabled" : ""}>
                  ${product.available ? "ADD" : "Sold Out"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  addConstructorTrackingToExistingProducts() {
    const popularProductsWrapper = document.getElementById("search-popular-products-wrapper");
    if (popularProductsWrapper) {
      const existingProducts = popularProductsWrapper.querySelectorAll(".product-item");

      existingProducts.forEach((item, index) => {
        const productLink = item.querySelector('a[href*="/products/"]');
        if (productLink && !item.hasAttribute("data-cnstrc-tracking-added")) {
          const productHandle = productLink.href.split("/products/")[1]?.split("?")[0];

          if (productHandle) {
            item.setAttribute("data-cnstrc-item-id", productHandle);
            item.setAttribute("data-cnstrc-tracking-added", "true");

            productLink.addEventListener("click", function (e) {
              if (window.constructorio?.tracker) {
                window.constructorio.tracker.trackAutocompleteSelect({
                  term: productHandle,
                  section: "Products",
                });
              }
            });
          }
        }
      });
    }
  }

  addAutocompleteTrackingListeners(searchTerm) {
    const suggestionLinks = this.results.querySelectorAll(".autocomplete-suggestion");
    suggestionLinks.forEach((link, index) => {
      link.addEventListener("click", (e) => {
        const suggestion = e.target.dataset.suggestion;
        if (window.constructorio?.tracker) {
          window.constructorio.tracker.trackAutocompleteSelect({
            term: suggestion,
            original_query: searchTerm,
            section: "Search Suggestions",
          });
        }
      });
    });

    const productLinks = this.results.querySelectorAll(".product-item .product-link");
    productLinks.forEach((link, index) => {
      link.addEventListener("click", (e) => {
        const productItem = e.target.closest(".product-item");
        const itemId = productItem?.dataset.cnstrcItemId;
        const itemName = e.target.textContent.trim();

        if (itemId && itemName && window.constructorio?.tracker) {
          window.constructorio.tracker.trackAutocompleteSelect({
            term: itemName,
            original_query: searchTerm,
            section: "Products",
            item_id: itemId,
          });
        }
      });
    });
  }

  async getShopifySearchResults(searchTerm) {
    let resourcesType = "product";

    if (this.input.dataset.searchCollections === "true") {
      resourcesType = `${resourcesType},collection`;
    }
    if (this.input.dataset.searchPages === "true") {
      resourcesType = `${resourcesType},page`;
    }
    if (this.input.dataset.searchArticles === "true") {
      resourcesType = `${resourcesType},article`;
    }

    const response = await fetch(
      `/search/suggest?q=${searchTerm}&resources[type]=${resourcesType}&resources[limit]=10&section_id=predictive-search`
    );

    if (!response.ok) {
      const error = new Error(response.status);
      this.close();
      throw error;
    }

    const text = await response.text();
    const resultsMarkup = new DOMParser()
      .parseFromString(text, "text/html")
      .querySelector("#shopify-section-predictive-search").innerHTML;
    this.results.innerHTML = resultsMarkup;

    this.addConstructorTrackingToExistingProducts();

    this.open();
  }

  open() {
    if (!this.results) {
      return;
    }

    this.results.style.display = "block";

    const countResults = this.results.querySelectorAll(".product-item").length;
    const hasSuggestions = this.results.querySelector(".autocomplete-suggestion");

    switch (countResults) {
      case 0:
        this.alert.textContent = this.alert.dataset.textNoResults;
        break;
      case 1:
        this.alert.textContent = this.alert.dataset.textResultFound;
        break;
      default:
        this.alert.textContent = this.alert.dataset.textResultsFound.replace("[count]", countResults);
        break;
    }

    this.footer.removeAttribute("hidden");

    window.SPR?.initDomEls();
    window.SPR?.loadBadges();

    if (countResults === 0 && hasSuggestions) {
      this.popularProducts?.removeAttribute("hidden");
    } else {
      this.popularProducts?.setAttribute("hidden", "hidden");
    }
  }

  close() {
    this.results.style.display = "none";
    this.alert.textContent = "";
    this.footer.setAttribute("hidden", "hidden");

    this.popularProducts?.removeAttribute("hidden");
  }

  speechRecognition() {
    // eslint-disable-next-line no-undef, no-use-before-define, no-var
    let SpeechRecognition = SpeechRecognition || webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.onstart = () => {
      this.speechBtn.classList.add("speech-started");

      setTimeout(() => {
        this.speechBtn.classList.remove("speech-started");
      }, 5000);
    };

    recognition.onspeechend = (event) => {
      this.speechBtn.classList.remove("speech-started");
    };

    recognition.onresult = (event) => {
      if (event.results) {
        this.input.value = event.results[0][0].transcript;
        this.onChange();
      }
    };

    recognition.start();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define("predictive-search", PredictiveSearch);

/**
 * SearchPageAutocomplete - Handles autocomplete for the main search page
 */
class SearchPageAutocomplete {
  constructor() {
    this.input = document.getElementById("main-search-input");
    this.results = document.getElementById("main-search-autocomplete");
    this.form = document.getElementById("main-search-form");

    if (!this.input || !this.results) {
      return;
    }

    this.isVisible = false;
    this.debounceTimer = null;

    this.bindEvents();
  }

  bindEvents() {
    // Input events
    this.input.addEventListener("input", (e) => this.onInput(e));
    this.input.addEventListener("focus", () => this.onFocus());
    this.input.addEventListener("blur", (e) => this.onBlur(e));

    // Form submission
    this.form.addEventListener("submit", (e) => this.onSubmit(e));

    // Close on escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isVisible) {
        this.hide();
        this.input.blur();
      }
    });

    // Close when clicking outside
    document.addEventListener("click", (e) => {
      if (!this.input.contains(e.target) && !this.results.contains(e.target)) {
        this.hide();
      }
    });
  }

  onInput(e) {
    const query = e.target.value.trim();

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (query.length >= 2) {
        this.getAutocompleteResults(query);
      } else {
        this.hide();
      }
    }, 300);
  }

  onFocus() {
    const query = this.input.value.trim();
    if (query.length >= 2) {
      this.getAutocompleteResults(query);
    }
  }

  onBlur(e) {
    // Delay hiding to allow clicks on results
    setTimeout(() => {
      if (!this.results.contains(document.activeElement)) {
        this.hide();
      }
    }, 150);
  }

  onSubmit(e) {
    const query = this.input.value.trim();
    if (query && window.constructorio?.tracker) {
      window.constructorio.tracker.trackSearchSubmit({
        term: query,
        section: "Products",
      });
    }
    this.hide();
  }

  async getAutocompleteResults(searchTerm) {
    try {
      if (!window.constructorio?.autocomplete) {
        console.warn("Constructor.io autocomplete not available");
        return;
      }

      // Track input focus
      if (window.constructorio.tracker) {
        window.constructorio.tracker.trackInputFocus({
          term: searchTerm,
          section: "Products",
        });
      }

      const response = await window.constructorio.autocomplete.getAutocompleteResults(searchTerm, {
        resultsPerSection: {
          Products: 6,
          "Search Suggestions": 5,
        },
        section: "Products",
      });

      this.renderResults(response, searchTerm);
      this.show();
    } catch (error) {
      console.error("Autocomplete error:", error);
      this.hide();
    }
  }

  renderResults(response, searchTerm) {
    if (!response.sections) {
      this.hide();
      return;
    }

    let html = "";

    // Search suggestions
    if (response.sections["Search Suggestions"]?.length > 0) {
      html += `
        <div class="autocomplete-section">
          <h6>Suggestions</h6>
          <ul>
            ${response.sections["Search Suggestions"]
              .map(
                (suggestion, index) => `
              <li>
                <a href="/search?q=${encodeURIComponent(suggestion.value)}" 
                   class="autocomplete-suggestion"
                   data-suggestion="${suggestion.value}"
                   data-index="${index}"
                   data-cnstrc-item-section="Search Suggestions"
                   data-cnstrc-item-id="${suggestion.value}"
                   data-cnstrc-item-name="${suggestion.value}">
                  ${suggestion.value}
                </a>
              </li>
            `
              )
              .join("")}
          </ul>
        </div>
      `;
    }

    // Products
    if (response.sections.Products?.length > 0) {
      html += `
        <div class="autocomplete-section">
          <h6>Products</h6>
          <ul>
            ${response.sections.Products.map((product, index) => {
              const data = product.data || product;
              const title = product.value || data.display_name || data.title || data.name || "";
              const url = data.url || `/products/${data.handle || data.id || ""}`;
              const image = data.image_url || data.image || "";
              const price = data.price || data.display_price || "";
              const itemId = data.id || data.product_id || data.item_id || data.handle || "";

              const formattedPrice = price ? `$${parseFloat(price).toFixed(2)}` : "";

              return `
                <li>
                  <a href="${url}" 
                     class="autocomplete-product"
                     data-cnstrc-item-id="${itemId}" 
                     data-cnstrc-item-name="${title}" 
                     data-cnstrc-item-section="Products">
                    ${image ? `<img src="${image}" alt="${title}">` : `<div class="placeholder-image"></div>`}
                    <div class="product-info">
                      <span class="product-name">${title}</span>
                      ${formattedPrice ? `<span class="product-price">${formattedPrice}</span>` : ""}
                    </div>
                  </a>
                </li>
              `;
            }).join("")}
          </ul>
        </div>
      `;
    }

    if (html) {
      this.results.innerHTML = html;
      this.addClickTracking(searchTerm);
    } else {
      this.hide();
    }
  }

  addClickTracking(searchTerm) {
    // Track suggestion clicks
    this.results.querySelectorAll(".autocomplete-suggestion").forEach((link, index) => {
      link.addEventListener("click", () => {
        if (window.constructorio?.tracker) {
          window.constructorio.tracker.trackAutocompleteSelect({
            term: link.dataset.suggestion,
            section: "Search Suggestions",
            originalQuery: searchTerm,
          });
        }
      });
    });

    // Track product clicks
    this.results.querySelectorAll(".autocomplete-product").forEach((link, index) => {
      link.addEventListener("click", () => {
        if (window.constructorio?.tracker) {
          window.constructorio.tracker.trackAutocompleteSelect({
            term: link.dataset.cnstrcItemId,
            section: "Products",
            originalQuery: searchTerm,
          });
        }
      });
    });
  }

  show() {
    this.results.style.display = "block";
    this.results.classList.remove("d-none");
    this.isVisible = true;
  }

  hide() {
    this.results.style.display = "none";
    this.results.classList.add("d-none");
    this.isVisible = false;
  }
}

window.closeSearchOffcanvas = (btn, event) => {
  setTimeout(() => {
    bootstrap.Offcanvas.getOrCreateInstance("#offcanvas-search").hide();
  }, 300);
};

// Initialize search page autocomplete when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  if (document.getElementById("main-search-input")) {
    // Wait for Constructor.io to be ready
    const initSearchPageAutocomplete = () => {
      if (window.constructorio) {
        new SearchPageAutocomplete();
      } else {
        setTimeout(initSearchPageAutocomplete, 100);
      }
    };
    initSearchPageAutocomplete();
  }
});
